import React, { useState } from 'react';
import { Shield, CheckCircle, ExternalLink } from 'lucide-react';
import EgovexLogo from './EgovexLogo';
import { setAnalyticsConsent } from '../src/lib/analytics';

interface ConsentGateProps {
  lang: 'en' | 'it' | 'es' | 'de' | 'fr';
  onAccept: (preferredLanguage: string) => void;
  onShowPrivacy: () => void;
  onShowTerms: () => void;
}

const CONSENT_STRINGS: Record<string, {
  title: string;
  subtitle: string;
  termsPrivacy: string;
  termsPrivacyLink1: string;
  termsPrivacyLink2: string;
  aiProcessing: string;
  aiProcessingDesc: string;
  marketing: string;
  marketingDesc: string;
  continue: string;
  required: string;
}> = {
  en: {
    title: 'Welcome to Egovex',
    subtitle: 'Before you start, please review and accept our terms.',
    termsPrivacy: 'I accept the',
    termsPrivacyLink1: 'Terms of Service',
    termsPrivacyLink2: 'Privacy Policy',
    aiProcessing: 'I consent to AI data processing',
    aiProcessingDesc: 'Egovex uses AI to analyze your entries and provide personalized insights. Your data is processed securely and never shared.',
    marketing: 'Keep me updated (optional)',
    marketingDesc: 'Receive occasional updates about new features and improvements.',
    continue: 'Continue to Egovex',
    required: 'Required to use Egovex'
  },
  it: {
    title: 'Benvenuto in Egovex',
    subtitle: 'Prima di iniziare, leggi e accetta i nostri termini.',
    termsPrivacy: 'Accetto i',
    termsPrivacyLink1: 'Termini di Servizio',
    termsPrivacyLink2: 'Privacy Policy',
    aiProcessing: 'Acconsento al trattamento AI dei dati',
    aiProcessingDesc: 'Egovex usa l\'AI per analizzare i tuoi momenti e fornire insight personalizzati. I tuoi dati sono trattati in sicurezza e mai condivisi.',
    marketing: 'Tienimi aggiornato (opzionale)',
    marketingDesc: 'Ricevi aggiornamenti occasionali su nuove funzionalità e miglioramenti.',
    continue: 'Continua verso Egovex',
    required: 'Necessario per usare Egovex'
  },
  es: {
    title: 'Bienvenido a Egovex',
    subtitle: 'Antes de comenzar, revisa y acepta nuestros términos.',
    termsPrivacy: 'Acepto los',
    termsPrivacyLink1: 'Términos de Servicio',
    termsPrivacyLink2: 'Política de Privacidad',
    aiProcessing: 'Consiento el procesamiento de datos por IA',
    aiProcessingDesc: 'Egovex usa IA para analizar tus entradas y proporcionar insights personalizados. Tus datos se procesan de forma segura.',
    marketing: 'Mantenerme actualizado (opcional)',
    marketingDesc: 'Recibe actualizaciones ocasionales sobre nuevas funciones y mejoras.',
    continue: 'Continuar a Egovex',
    required: 'Necesario para usar Egovex'
  },
  de: {
    title: 'Willkommen bei Egovex',
    subtitle: 'Bevor Sie beginnen, lesen und akzeptieren Sie unsere Bedingungen.',
    termsPrivacy: 'Ich akzeptiere die',
    termsPrivacyLink1: 'Nutzungsbedingungen',
    termsPrivacyLink2: 'Datenschutzrichtlinie',
    aiProcessing: 'Ich stimme der KI-Datenverarbeitung zu',
    aiProcessingDesc: 'Egovex nutzt KI, um Ihre Einträge zu analysieren und personalisierte Einblicke zu geben. Ihre Daten werden sicher verarbeitet.',
    marketing: 'Auf dem Laufenden halten (optional)',
    marketingDesc: 'Erhalten Sie gelegentlich Updates zu neuen Funktionen und Verbesserungen.',
    continue: 'Weiter zu Egovex',
    required: 'Erforderlich für die Nutzung von Egovex'
  },
  fr: {
    title: 'Bienvenue sur Egovex',
    subtitle: 'Avant de commencer, veuillez lire et accepter nos conditions.',
    termsPrivacy: 'J\'accepte les',
    termsPrivacyLink1: 'Conditions d\'utilisation',
    termsPrivacyLink2: 'Politique de confidentialité',
    aiProcessing: 'Je consens au traitement des données par l\'IA',
    aiProcessingDesc: 'Egovex utilise l\'IA pour analyser vos entrées et fournir des insights personnalisés. Vos données sont traitées en toute sécurité.',
    marketing: 'Me tenir informé (optionnel)',
    marketingDesc: 'Recevez des mises à jour occasionnelles sur les nouvelles fonctionnalités.',
    continue: 'Continuer vers Egovex',
    required: 'Requis pour utiliser Egovex'
  }
};

const ConsentGate: React.FC<ConsentGateProps> = ({ lang, onAccept, onShowPrivacy, onShowTerms }) => {
  const t = CONSENT_STRINGS[lang] || CONSENT_STRINGS.en;
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [aiProcessingAccepted, setAiProcessingAccepted] = useState(false);
  const [marketingAccepted, setMarketingAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canContinue = termsAccepted && aiProcessingAccepted;

  const handleContinue = async () => {
    if (!canContinue) return;
    
    setIsSubmitting(true);
    try {
      const response = await fetch('/api/consents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          termsAccepted: true,
          privacyAccepted: true,
          aiProcessingConsent: true,
          marketingConsent: marketingAccepted,
          preferredLanguage: lang,
        }),
      });

      if (response.ok) {
        setAnalyticsConsent(true);
        localStorage.setItem('egovex_preferred_language', lang);
        onAccept(lang);
      }
    } catch (error) {
      console.error('Error saving consents:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-indigo-50/30 to-purple-50/30 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-xl border border-black/5 p-8">
        <div className="flex items-center gap-3 mb-6">
          <EgovexLogo size={48} />
          <div>
            <h1 className="text-2xl font-black tracking-tight text-[#1D1D1F]">{t.title}</h1>
            <p className="text-sm text-black/50 font-medium">{t.subtitle}</p>
          </div>
        </div>

        <div className="space-y-5 mb-8">
          <label className="flex items-start gap-3 cursor-pointer group">
            <div className="relative mt-0.5">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="sr-only"
              />
              <div className={`w-5 h-5 rounded-md border-2 transition-all flex items-center justify-center ${termsAccepted ? 'bg-indigo-600 border-indigo-600' : 'border-black/20 group-hover:border-indigo-400'}`}>
                {termsAccepted && <CheckCircle className="w-3.5 h-3.5 text-white" />}
              </div>
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-[#1D1D1F]">
                {t.termsPrivacy}{' '}
                <button onClick={(e) => { e.preventDefault(); onShowTerms(); }} className="text-indigo-600 hover:underline inline-flex items-center gap-0.5">
                  {t.termsPrivacyLink1} <ExternalLink className="w-3 h-3" />
                </button>{' '}
                &{' '}
                <button onClick={(e) => { e.preventDefault(); onShowPrivacy(); }} className="text-indigo-600 hover:underline inline-flex items-center gap-0.5">
                  {t.termsPrivacyLink2} <ExternalLink className="w-3 h-3" />
                </button>
              </p>
              <p className="text-xs text-black/40 mt-0.5">{t.required}</p>
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer group">
            <div className="relative mt-0.5">
              <input
                type="checkbox"
                checked={aiProcessingAccepted}
                onChange={(e) => setAiProcessingAccepted(e.target.checked)}
                className="sr-only"
              />
              <div className={`w-5 h-5 rounded-md border-2 transition-all flex items-center justify-center ${aiProcessingAccepted ? 'bg-indigo-600 border-indigo-600' : 'border-black/20 group-hover:border-indigo-400'}`}>
                {aiProcessingAccepted && <CheckCircle className="w-3.5 h-3.5 text-white" />}
              </div>
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-[#1D1D1F]">{t.aiProcessing}</p>
              <p className="text-xs text-black/50 mt-0.5">{t.aiProcessingDesc}</p>
              <p className="text-xs text-black/40 mt-0.5">{t.required}</p>
            </div>
          </label>

          <div className="border-t border-black/5 pt-4">
            <label className="flex items-start gap-3 cursor-pointer group">
              <div className="relative mt-0.5">
                <input
                  type="checkbox"
                  checked={marketingAccepted}
                  onChange={(e) => setMarketingAccepted(e.target.checked)}
                  className="sr-only"
                />
                <div className={`w-5 h-5 rounded-md border-2 transition-all flex items-center justify-center ${marketingAccepted ? 'bg-indigo-600 border-indigo-600' : 'border-black/20 group-hover:border-indigo-400'}`}>
                  {marketingAccepted && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                </div>
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-[#1D1D1F]">{t.marketing}</p>
                <p className="text-xs text-black/50 mt-0.5">{t.marketingDesc}</p>
              </div>
            </label>
          </div>
        </div>

        <button
          onClick={handleContinue}
          disabled={!canContinue || isSubmitting}
          className={`w-full flex items-center justify-center gap-2 px-6 py-4 rounded-2xl font-bold text-white transition-all ${canContinue ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:opacity-90 active:scale-[0.98]' : 'bg-black/20 cursor-not-allowed'}`}
        >
          <Shield className="w-5 h-5" />
          {isSubmitting ? '...' : t.continue}
        </button>

        <p className="text-center text-xs text-black/30 mt-4 font-medium">
          GDPR & AI Act Compliant
        </p>
      </div>
    </div>
  );
};

export default ConsentGate;
